import { Theme, Tooltip, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import {
  AggregationItem,
  AggregationColumn,
  ColumnType,
  Nullable,
} from 'common-types';
import CustomLink from './elements/Link';
import User from './elements/User';
import { formatDistanceToNow } from 'date-fns';
import { createNewDate } from './aggregation-utils';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import vs from 'react-syntax-highlighter/dist/cjs/styles/prism/vsc-dark-plus';
import { makeStyles } from 'tss-react/mui';
import { getCurrentThemeType } from '../../common/themes';
import prism from 'react-syntax-highlighter/dist/cjs/styles/prism/duotone-light';
import { getCountryByCode } from '../../common/common-utils';

const AggregatedContentResolver: FC<
  React.PropsWithChildren<ContentResolverProps>
> = ({ item, column }) => {
  let content: Nullable<ReactNode> = null;
  const value = item[column.key];
  const date = createNewDate(value);
  const { classes, cx } = useStyles();
  const currentTheme = getCurrentThemeType();

  if (column.href) {
    content = (
      <Tooltip title={value} className={classes.link}>
        <div>
          <Typography
            variant='body2'
            color='text.primary'
            className={cx(classes.linkText, classes.overflow)}>
            {value}
          </Typography>
          <CustomLink
            textClassName={classes.overflow}
            href={item[column.href]}
            value={''}
          />
        </div>
      </Tooltip>
    );
  } else if (column.type === ColumnType.User) {
    content = (
      <User
        value={value}
        userLink={item.userLink}
        userAvatar={item.userAvatar}
      />
    );
  } else if (column.type === ColumnType.Date) {
    if (date) {
      content = (
        <Typography variant='caption' color='text.primary'>
          {date && formatDistanceToNow(date) + ' ago'}
        </Typography>
      );
    }
  } else if (column.type === ColumnType.Location) {
    const country = getCountryByCode(value);
    content = (
      <Tooltip
        title={
          country ? (
            <Typography variant='body2' color='text.primary'>
              {country}
            </Typography>
          ) : (
            ''
          )
        }>
        <div className={classes.flagContent}>
          {value && (
            <img
              className={classes.flag}
              alt={country}
              src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${value}.svg`}
            />
          )}
        </div>
      </Tooltip>
    );
  } else {
    content = (
      <Typography
        variant='caption'
        color='text.primary'
        className={classes.overflow}>
        {value}
      </Typography>
    );
  }

  if (column.type === ColumnType.Match && column.tooltip) {
    const snippetCode = item[column.tooltip];
    if (snippetCode !== '') {
      content = (
        <Tooltip
          arrow
          componentsProps={{
            tooltip: { sx: { maxWidth: window.innerWidth } },
          }}
          title={
            <SyntaxHighlighter
              language={item.language ? item.language : 'javascript'}
              style={currentTheme === 'dark' ? vs : prism}
              startingLineNumber={
                item.snippetLineNumber ? Number(item.snippetLineNumber) : 1
              }
              showLineNumbers>
              {snippetCode}
            </SyntaxHighlighter>
          }>
          <div className={classes.overflow}>{item.match}</div>
        </Tooltip>
      );
    }
  } else if (column.tooltip === ColumnType.Date) {
    const date = createNewDate(item[column.tooltip]);
    if (date) {
      content = (
        <Tooltip
          arrow
          title={
            <Typography variant='caption' color='text.primary'>
              {`${date.toLocaleDateString()}, ${date.toLocaleTimeString()}`}
            </Typography>
          }>
          <Typography variant='caption' color='text.primary'>
            {date && formatDistanceToNow(date, { addSuffix: true })}
          </Typography>
        </Tooltip>
      );
    }
  } else if (column.type === ColumnType.DaysOpen) {
    const date = new Date();
    date.setDate(date.getDate() - Number(item[column.type]));
    content = (
      <Tooltip
        arrow
        title={
          <Typography variant='caption' color='text.primary'>
            {`${date.toLocaleDateString()}, ${date.toLocaleTimeString()}`}
          </Typography>
        }>
        <Typography variant='caption' color='text.primary'>
          {date && formatDistanceToNow(date, { addSuffix: true })}
        </Typography>
      </Tooltip>
    );
  } else if (column.tooltip) {
    content = (
      <Tooltip
        arrow
        title={
          <Typography
            variant='caption'
            color='text.primary'
            sx={{ whiteSpace: 'pre-wrap' }}>
            {item[column.tooltip]}
          </Typography>
        }>
        <Typography
          variant='caption'
          color='text.primary'
          className={classes.overflow}>
          {content}
        </Typography>
      </Tooltip>
    );
  }

  return content;
};

const useStyles = makeStyles()((theme: Theme) => ({
  overflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    lineClamp: 2,
    WebkitLineClamp: 2,
    wordBreak: 'break-word',
    WebkitBoxOrient: 'vertical',
  },
  link: {
    display: 'flex',
    flexDirection: 'row',
  },
  flag: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  flagContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkText: {
    // minWidth: '150px',
    // width: '150px',
  },
}));

export interface ContentResolverProps {
  item: AggregationItem;
  column: AggregationColumn;
}

export default AggregatedContentResolver;
